import { useState } from "react";
import { HashStraight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

export default function HashtagGenerator() {
  useSeo("YouTube Hashtag Generator", "Generate ready-to-paste YouTube hashtags from your video keywords in one click.");
  const [input, setInput] = useState("");
  const [tags, setTags] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    const keywords = input.split(",").map((k) => k.trim()).filter(Boolean);
    if (!keywords.length) return;
    setLoading(true);
    try {
      const res = await api.post("/hashtags", { keywords });
      setTags(res.data.hashtags);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="hashtag-generator" title="Hashtag Generator" tagline="Type your video keywords (comma-separated) and get an instant hashtag set." hasResults={!!tags}>
      <form onSubmit={generate} className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <label className="text-sm font-semibold block mb-2">Keywords</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="hashtag-keywords-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="cooking, pasta, italian food"
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button
            data-testid="hashtag-generate-btn"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <HashStraight size={16} weight="bold" /> {loading ? "Generating..." : "Generate"}
          </button>
        </div>
      </form>

      {tags && (
        <div className="mt-8 surface rounded-[20px] p-6 sm:p-8 fade-up">
          <div className="flex flex-wrap gap-2" data-testid="hashtag-list">
            {tags.map((t) => (
              <span key={t} className="px-3.5 py-1.5 rounded-full bg-[var(--coral)]/12 border border-[var(--coral)]/30 text-sm">
                {t}
              </span>
            ))}
          </div>
          <div className="mt-6">
            <CopyButton text={tags.join(" ")} label={`Copy all ${tags.length} hashtags`} testId="copy-all-hashtags-btn" />
          </div>
        </div>
      )}
    </ToolShell>
  );
}
